import { useCallback, useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react';
import { Encounter, Patient } from '@medplum/fhirtypes';

type PreviousRecommendation = {
  date: string;
  text: string;
};

export const useEncounterRecommendations = (
  patient?: Patient,
  encounter?: Encounter
) => {
  const medplum = useMedplum();
  const [recommendation, setRecommendation] = useState('');
  const [initialRecommendation, setInitialRecommendation] = useState('');
  const [carePlanId, setCarePlanId] = useState<string | undefined>(undefined);
  const [previousRecommendations, setPreviousRecommendations] = useState<
    PreviousRecommendation[]
  >([]);
  const [loading, setLoading] = useState(false);

  // Load current recommendation from CarePlan
  useEffect(() => {
    if (!encounter?.id || !patient?.id) {
      return;
    }

    const fetchCurrentRecommendation = async () => {
      setLoading(true);
      try {
        // Search for CarePlan linked to this encounter
        const carePlan = await medplum.searchOne('CarePlan', {
          encounter: `Encounter/${encounter.id}`,
          subject: `Patient/${patient.id}`,
          status: 'active',
        });

        if (carePlan) {
          // Get recommendation text from the first note
          const noteText = carePlan.note?.[0]?.text || '';

          setCarePlanId(carePlan.id);
          setRecommendation(noteText);
          setInitialRecommendation(noteText);
        } else {
          // No CarePlan exists yet for this encounter
          setCarePlanId(undefined);
          setRecommendation('');
          setInitialRecommendation('');
        }
      } catch (error) {
        console.error('Error loading recommendation:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentRecommendation();
  }, [encounter, patient, medplum]);

  // Fetch all previous recommendations from CarePlans
  useEffect(() => {
    if (!patient?.id || !encounter?.id) {
      return;
    }

    const fetchAllPrevious = async () => {
      try {
        // Fetch all CarePlans for this patient
        const carePlans = await medplum.searchResources('CarePlan', {
          subject: `Patient/${patient.id}`,
          _sort: '-date',
          _count: '100',
        });

        const recommendations: PreviousRecommendation[] = [];

        for (const carePlan of carePlans) {
          // Skip the current encounter's CarePlan
          const carePlanEncounterId =
            carePlan.encounter?.reference?.split('/')[1];
          if (carePlanEncounterId === encounter.id) {
            continue;
          }

          const noteText = carePlan.note?.[0]?.text;

          if (noteText && carePlan.created) {
            recommendations.push({
              date: carePlan.created,
              text: noteText,
            });
          }
        }

        setPreviousRecommendations(recommendations);
      } catch (error) {
        console.error('Error fetching previous recommendations:', error);
      }
    };

    fetchAllPrevious();
  }, [patient, encounter, medplum]);

  const updateRecommendation = useCallback((value: string) => {
    setRecommendation(value);
  }, []);

  const updateInitialRecommendation = useCallback((value: string) => {
    setInitialRecommendation(value);
  }, []);

  return {
    recommendation,
    initialRecommendation,
    carePlanId,
    previousRecommendations,
    loading,
    updateRecommendation,
    updateInitialRecommendation,
  };
};
